'use client';

import { useEffect, useMemo, useState, useCallback } from 'react';
import { ProductInterface } from '@qlevr/shared/interfaces';
import PersonalizedProductMoreInfoButton from './personalized-product-more-info-button';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useProduct } from '@qlevr/shared/providers';
import { useSearchParams } from 'next/navigation';

export interface PersonalizedProductProps {
  product: ProductInterface;
}

export default function PersonalizedProduct({ product }: PersonalizedProductProps) {
  const { setAttributes, attributes, setSelectedVariant } = useProduct();
  const t = useTranslations();
  const [isWiggling, setIsWiggling] = useState(false);

  const searchParams = useSearchParams();
  const size = searchParams.get('size');
  const color = searchParams.get('color');
  const relatedCustomizableProduct = product.relatedCustomizableProduct;

  const attributeKey = t('pdp.attributes.personalizationKey');
  const maxLength = 15;
  const allowedCharRegex = /^[a-zA-Z0-9À-ÿ\-´^ ]+$/;

  const personalizationValue = attributes.find((attr) => attr.key === attributeKey)?.value || '';

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;

      if (value === '') {
        setAttributes((prevAttributes) => prevAttributes.filter((attr) => attr.key !== attributeKey));
        return;
      }

      if (!allowedCharRegex.test(value) || value.length > maxLength) {
        setIsWiggling(true);
        setTimeout(() => setIsWiggling(false), 300);
      }

      // Sanitize input
      value = value.replace(/[^a-zA-Z0-9À-ÿ\-´^ ]/g, '').slice(0, maxLength);

      setAttributes((prevAttributes) => {
        const updatedAttributes = prevAttributes.filter((attr) => attr.key !== attributeKey);
        if (value.trim()) {
          updatedAttributes.push({ key: attributeKey, value });
        }
        return updatedAttributes;
      });
    },
    [setAttributes, attributeKey, maxLength],
  );

  const matchingVariant = useMemo(() => {
    if (!relatedCustomizableProduct?.reference?.variants) return null;

    return relatedCustomizableProduct.reference.variants.find((variant) => {
      const optionsMap = new Map(variant.options.map((opt) => [opt.name.toLowerCase(), opt.value]));

      return (!color || optionsMap.get('color') === color) && (!size || optionsMap.get('size') === size);
    });
  }, [relatedCustomizableProduct, color, size]);

  useEffect(() => {
    if (matchingVariant?.availableForSale) {
      if (personalizationValue) {
        setSelectedVariant(matchingVariant);
      } else {
        setSelectedVariant(null);
      }
    } else {
      setSelectedVariant(null);
      setAttributes((prevAttributes) => prevAttributes.filter((attr) => attr.key !== attributeKey));
    }
  }, [personalizationValue, matchingVariant, setSelectedVariant, setAttributes]);

  if (!relatedCustomizableProduct || !matchingVariant?.availableForSale) {
    return null;
  }

  return (
    <div id="customize" className="space-y-3">
      <div className="flex flex-wrap justify-between gap-x-4">
        <div className="flex gap-2">
          <label htmlFor="personalization-input" className="font-bold">
            {t('pdp.personalizeProduct.title')}
          </label>
        </div>
        <PersonalizedProductMoreInfoButton product={product} />
      </div>
      <div className="flex flex-col gap-2">
        <div className={clsx('relative', isWiggling && 'wiggle')}>
          <input
            id="personalization-input"
            type="text"
            className="w-full rounded-full border border-black py-3.5 pl-6 pr-16 placeholder:text-slate-400 focus:border-black focus:ring-black focus:ring-opacity-50"
            placeholder={t('pdp.personalizeProduct.inputPlaceholder')}
            name={attributeKey}
            value={personalizationValue}
            onChange={handleInputChange}
          />
          <span className="absolute right-6 top-3.5">
            {personalizationValue.length}/{maxLength}
          </span>
        </div>
      </div>
    </div>
  );
}
